// src/pages/Dashboard/Admin/SeasonStats/utils/seasonStatsDashboardUtils.js

export const STAT_TYPES = {
  TOTAL: "TOTAL",
  GENDER: "GENDER",
  AGE_RANGE: "AGE_RANGE",
  GROUP: "GROUP",
  DISCIPLINE: "DISCIPLINE",
};

function createClientId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function toStatCode(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function getSeasonStartYear(season = "") {
  const match = String(season).match(/^(\d{4})/);

  if (!match) return 0;

  return Number(match[1]) || 0;
}

export function sortSeasonStats(rows = []) {
  return [...rows].sort((a, b) => {
    return getSeasonStartYear(b?.season) - getSeasonStartYear(a?.season);
  });
}

export function normalizeSeasonStat(row = {}) {
  const breakdowns = Array.isArray(row?.breakdowns)
    ? row.breakdowns
    : [];

  const breakdownsByType = breakdowns.reduce((acc, item) => {
    const type = String(item?.breakdown_type || "");

    if (!type) return acc;

    if (!acc[type]) {
      acc[type] = [];
    }

    acc[type].push(item);

    return acc;
  }, {});

  Object.keys(breakdownsByType).forEach((type) => {
    breakdownsByType[type] = [...breakdownsByType[type]].sort(
      (a, b) =>
        Number(a?.sort_order || 0) - Number(b?.sort_order || 0),
    );
  });

  return {
    ...row,
    breakdowns,
    breakdowns_by_type: breakdownsByType,
  };
}

export function getBreakdownsByType(seasonStat, type) {
  if (!seasonStat) return [];

  if (seasonStat?.breakdowns_by_type?.[type]) {
    return seasonStat.breakdowns_by_type[type];
  }

  return (seasonStat?.breakdowns || [])
    .filter((item) => item?.breakdown_type === type)
    .sort(
      (a, b) =>
        Number(a?.sort_order || 0) - Number(b?.sort_order || 0),
    );
}

export function getStatValue(
  seasonStat,
  breakdownType,
  code,
) {
  const rows = getBreakdownsByType(
    seasonStat,
    breakdownType,
  );

  const item = rows.find(
    (row) => String(row?.code || "") === String(code || ""),
  );

  return Number(item?.value) || 0;
}

export function getGroupRows(seasonStat) {
  return getBreakdownsByType(
    seasonStat,
    STAT_TYPES.GROUP,
  ).map((item) => {
    const value = Number(item?.value) || 0;

    const capacity =
      item?.capacity === null ||
      item?.capacity === undefined
        ? null
        : Number(item.capacity);

    const fillRate =
      capacity && capacity > 0
        ? Math.round((value / capacity) * 100)
        : null;

    return {
      ...item,
      value,
      capacity,
      fillRate,
    };
  });
}

export function calculateSeasonSummary(seasonStat) {
  const total = getStatValue(
    seasonStat,
    STAT_TYPES.TOTAL,
    "ADHERENTS",
  );

  const male = getStatValue(
    seasonStat,
    STAT_TYPES.GENDER,
    "MALE",
  );

  const female = getStatValue(
    seasonStat,
    STAT_TYPES.GENDER,
    "FEMALE",
  );

  const genderTotal = male + female;

  const malePercent =
    genderTotal > 0
      ? Math.round((male / genderTotal) * 100)
      : 0;

  const femalePercent =
    genderTotal > 0
      ? Math.round((female / genderTotal) * 100)
      : 0;

  const groups = getGroupRows(seasonStat);

  const groupsWithCapacity = groups.filter(
    (group) =>
      Number.isFinite(group.capacity) &&
      group.capacity > 0,
  );

  const averageFillRate =
    groupsWithCapacity.length > 0
      ? Math.round(
          groupsWithCapacity.reduce(
            (sum, group) => sum + (group.fillRate || 0),
            0,
          ) / groupsWithCapacity.length,
        )
      : 0;

  const fullGroups = groups.filter(
    (group) =>
      group.fillRate !== null && group.fillRate >= 100,
  ).length;

  return {
    total,
    male,
    female,
    malePercent,
    femalePercent,
    groupsCount: groups.length,
    averageFillRate,
    fullGroups,
  };
}

export function buildEvolutionData(rows = []) {
  return [...rows]
    .map((item) => ({
      season: item?.season || "—",
      total: Number(item?.total_adherents) || 0,
      male: Number(item?.male) || 0,
      female: Number(item?.female) || 0,
    }))
    .sort(
      (a, b) =>
        getSeasonStartYear(a.season) -
        getSeasonStartYear(b.season),
    );
}

export function getChartData(seasonStat, type) {
  return getBreakdownsByType(seasonStat, type).map(
    (item) => ({
      name: item?.label || item?.code || "—",
      value: Number(item?.value) || 0,
      code: item?.code,
    }),
  );
}

export function getSourceLabel(source) {
  const labels = {
    AUTO: "Automatique",
    MANUAL: "Manuelle",
    MIXED: "Mixte",
  };

  return labels[source] || "Non renseignée";
}

export function getSourceClass(source) {
  const classes = {
    AUTO:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",

    MANUAL:
      "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",

    MIXED:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
  };

  return (
    classes[source] ||
    "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
  );
}

export function createDefaultBreakdowns() {
  return [
    {
      client_id: createClientId(),
      breakdown_type: "TOTAL",
      code: "ADHERENTS",
      label: "Adhérents",
      value: 0,
      capacity: null,
      sort_order: 1,
    },

    {
      client_id: createClientId(),
      breakdown_type: "GENDER",
      code: "MALE",
      label: "Garçons",
      value: 0,
      capacity: null,
      sort_order: 1,
    },

    {
      client_id: createClientId(),
      breakdown_type: "GENDER",
      code: "FEMALE",
      label: "Filles",
      value: 0,
      capacity: null,
      sort_order: 2,
    },

    {
      client_id: createClientId(),
      breakdown_type: "AGE_RANGE",
      code: "AGE_0_5",
      label: "0-5 ans",
      value: 0,
      capacity: null,
      sort_order: 1,
    },

    {
      client_id: createClientId(),
      breakdown_type: "AGE_RANGE",
      code: "AGE_6_7",
      label: "6-7 ans",
      value: 0,
      capacity: null,
      sort_order: 2,
    },

    {
      client_id: createClientId(),
      breakdown_type: "AGE_RANGE",
      code: "AGE_8_9",
      label: "8-9 ans",
      value: 0,
      capacity: null,
      sort_order: 3,
    },

    {
      client_id: createClientId(),
      breakdown_type: "AGE_RANGE",
      code: "AGE_10_11",
      label: "10-11 ans",
      value: 0,
      capacity: null,
      sort_order: 4,
    },

    {
      client_id: createClientId(),
      breakdown_type: "AGE_RANGE",
      code: "AGE_12_14",
      label: "12-14 ans",
      value: 0,
      capacity: null,
      sort_order: 5,
    },

    {
      client_id: createClientId(),
      breakdown_type: "AGE_RANGE",
      code: "AGE_15_17",
      label: "15-17 ans",
      value: 0,
      capacity: null,
      sort_order: 6,
    },

    {
      client_id: createClientId(),
      breakdown_type: "AGE_RANGE",
      code: "AGE_18_PLUS",
      label: "18 ans et +",
      value: 0,
      capacity: null,
      sort_order: 7,
    },
  ];
}

export function createEmptyBreakdown(type) {
  return {
    client_id: createClientId(),
    breakdown_type: type,
    code: "",
    label: "",
    value: 0,
    capacity: type === "GROUP" ? "" : null,
    sort_order: 999,
  };
}

export function prepareBreakdownsForEditor(rows = []) {
  return rows.map((item) => ({
    ...item,
    client_id:
      item?.client_id ||
      `db-${item?.id || createClientId()}`,
  }));
}

export function sanitizeBreakdowns(rows = []) {
  const counters = {};

  return rows
    .filter(
      (item) =>
        String(item?.breakdown_type || "").trim() &&
        String(item?.code || "").trim() &&
        String(item?.label || "").trim(),
    )
    .map((item) => {
      const type = String(item.breakdown_type)
        .trim()
        .toUpperCase();

      counters[type] = (counters[type] || 0) + 1;

      return {
        breakdown_type: type,

        code: String(item.code)
          .trim()
          .toUpperCase(),

        label: String(item.label).trim(),

        value: Math.max(
          0,
          Number(item.value) || 0,
        ),

        capacity:
          item.capacity === "" ||
          item.capacity === null ||
          item.capacity === undefined
            ? null
            : Math.max(
                0,
                Number(item.capacity) || 0,
              ),

        sort_order:
          Number(item.sort_order) > 0
            ? Number(item.sort_order)
            : counters[type],
      };
    });
}